/**
 * Permission Performance Monitor - Phase C
 * Tracks permission check metrics for optimization
 */

import { useEffect, useRef } from 'react';
import { logger } from '@/lib/logger';

interface PermissionCheckMetrics {
  operation: string;
  startTime: number;
  endTime: number;
  duration: number;
  success: boolean;
  cached: boolean;
}

class PermissionPerformanceMonitor {
  private metrics: PermissionCheckMetrics[] = [];
  private maxMetrics = 100; // Keep last 100 checks

  startCheck(operation: string): number {
    return performance.now();
  }

  endCheck(operation: string, startTime: number, success: boolean, cached: boolean): number {
    const endTime = performance.now();
    const duration = endTime - startTime;

    const metric: PermissionCheckMetrics = {
      operation,
      startTime,
      endTime,
      duration,
      success,
      cached
    };

    this.metrics.push(metric);
    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift();
    }

    // Log slow checks (>100ms)
    if (duration > 100) {
      logger.warn(`[PermissionPerformance] Slow check detected`, {
        operation,
        duration: `${duration.toFixed(2)}ms`
      });
    }

    return duration;
  }

  getStats() {
    if (this.metrics.length === 0) return null;

    const durations = this.metrics.map(m => m.duration);
    const cacheHits = this.metrics.filter(m => m.cached).length;
    
    // Calculate percentiles
    const sorted = [...durations].sort((a, b) => a - b);
    const p50 = sorted[Math.floor(sorted.length * 0.5)] || 0;
    const p95 = sorted[Math.floor(sorted.length * 0.95)] || 0;
    const p99 = sorted[Math.floor(sorted.length * 0.99)] || 0;

    return {
      totalChecks: this.metrics.length,
      cacheHitRate: (cacheHits / this.metrics.length) * 100,
      averageDuration: durations.reduce((sum, d) => sum + d, 0) / durations.length,
      p50,
      p95,
      p99,
      slowChecks: this.metrics.filter(m => m.duration > 100).length
    };
  }

  exportMetrics() {
    const stats = this.getStats();
    logger.info('[PermissionPerformance] Stats:', stats);
    return { stats, rawMetrics: this.metrics };
  }

  reset() {
    this.metrics = [];
  }
}

export const permissionPerformanceMonitor = new PermissionPerformanceMonitor();

/**
 * Hook to enable periodic performance logging
 * Use in admin panels or monitoring dashboards
 */
export const usePermissionPerformanceMonitor = (enabled: boolean = false) => {
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!enabled) return;

    // Log stats every 30 seconds
    intervalRef.current = setInterval(() => {
      const stats = permissionPerformanceMonitor.getStats();
      if (stats) {
        logger.debug('[PermissionPerformance] Periodic stats:', stats);
      }
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled]);
};
